import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Building2,
  ListChecks,
  Mail,
  MessageSquare,
  LogOut,
  ExternalLink,
  Users,
  Sparkles,
  Settings,
  FileText,
  ChevronLeft,
  Menu,
} from "lucide-react";
import { clearAccessToken, useIsAuthenticated } from "@/lib/auth";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/cms";
import { useDocumentTitle } from "@/lib/use-document-title";
import { useApplyPrimaryColor } from "@/lib/theme";

export function AdminLayout({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const [location] = useLocation();
  const isAuthed = useIsAuthenticated();
  const { brand } = useSiteSettings();
  // Mirror the brand primary colour in the admin shell too.
  useApplyPrimaryColor(brand.primaryColor);
  // Admin tab title from the live brand (was showing the static index.html
  // title — which previously leaked a competitor name).
  useDocumentTitle("Admin");

  // Collapsible sidebar — persisted so it survives navigations and reloads.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem("admin.sidebarCollapsed") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("admin.sidebarCollapsed", collapsed ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // The split-screen editors (site settings, project editor) manage their own
  // full-height layout, so they render edge-to-edge without the padded frame.
  const fullBleed =
    location.startsWith("/site-settings") || /^\/projects\/.+/.test(location);

  useEffect(() => {
    if (!isAuthed) {
      navigate("~/admin/login");
    }
  }, [isAuthed, navigate]);

  if (!isAuthed) return null;

  async function handleLogout() {
    try {
      await api("/api/auth/logout", { method: "POST" });
    } catch {
      /* ignore */
    }
    clearAccessToken();
    navigate("~/admin/login");
  }

  // location here is RELATIVE to "/admin" (Wouter nest), so root is "/"
  const isActive = (href: string) =>
    href === "/" ? location === "/" : location.startsWith(href);

  const NAV: ReadonlyArray<{
    href: string;
    icon: typeof LayoutDashboard;
    label: string;
    section?: string;
  }> = [
    { href: "/", icon: LayoutDashboard, label: "Áttekintés" },
    { href: "/messages", icon: Mail, label: "Üzenetek", section: "Beérkező" },
    { href: "/chat-log", icon: MessageSquare, label: "AI beszélgetések" },
    { href: "/projects", icon: Building2, label: "Projektek", section: "Tartalom" },
    { href: "/team", icon: Users, label: "Csapattagok" },
    { href: "/services", icon: Sparkles, label: "Szolgáltatások" },
    { href: "/pages", icon: FileText, label: "Statikus oldalak" },
    { href: "/site-settings", icon: Settings, label: "Oldal beállítások", section: "Beállítások" },
    { href: "/form-builder", icon: ListChecks, label: "Form szerkesztő" },
  ];

  return (
    <div className="min-h-screen flex bg-brand-50">
      <aside
        className={`${
          collapsed ? "w-16" : "w-60"
        } bg-white border-r border-brand-200 flex flex-col transition-[width] duration-200 shrink-0`}
      >
        <div className="h-[68px] border-b border-brand-200 flex items-center gap-2 px-3">
          {collapsed ? (
            <button
              type="button"
              onClick={() => setCollapsed(false)}
              title="Menü kinyitása"
              aria-label="Menü kinyitása"
              className="mx-auto p-1.5 rounded text-brand-500 hover:bg-brand-100 hover:text-brand-900"
            >
              <Menu size={18} />
            </button>
          ) : (
            <>
              <div className="w-8 h-8 rounded bg-brand-900 text-white grid place-items-center font-bold text-sm shrink-0">
                {brand.logoLetter}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-sm truncate">
                  {brand.shortName}
                </div>
                <div className="text-xs text-brand-500">Admin</div>
              </div>
              <button
                type="button"
                onClick={() => setCollapsed(true)}
                title="Menü becsukása"
                aria-label="Menü becsukása"
                className="p-1.5 rounded text-brand-500 hover:bg-brand-100 hover:text-brand-900 shrink-0"
              >
                <ChevronLeft size={18} />
              </button>
            </>
          )}
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {NAV.map(({ href, icon: Icon, label, section }) => (
            <div key={href}>
              {section &&
                (collapsed ? (
                  <div className="h-px bg-brand-200 mx-1.5 my-2" />
                ) : (
                  <div className="text-[10px] uppercase tracking-widish font-semibold text-brand-400 px-3 pt-4 pb-1.5">
                    {section}
                  </div>
                ))}
              <Link
                href={href}
                title={collapsed ? label : undefined}
                className={`flex items-center py-2 rounded-md text-sm font-medium transition-colors ${
                  collapsed ? "justify-center px-0" : "gap-2.5 px-3"
                } ${
                  isActive(href)
                    ? "bg-brand-100 text-brand-900"
                    : "text-brand-600 hover:bg-brand-50 hover:text-brand-900"
                }`}
              >
                <Icon size={16} className="shrink-0" />
                {!collapsed && label}
              </Link>
            </div>
          ))}
        </nav>

        <div className="p-3 border-t border-brand-200 space-y-1">
          <a
            href="/"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? "Megnyitás" : undefined}
            className={`flex items-center py-2 rounded-md text-sm font-medium text-brand-600 hover:bg-brand-50 hover:text-brand-900 ${
              collapsed ? "justify-center px-0" : "gap-2.5 px-3"
            }`}
          >
            <ExternalLink size={16} className="shrink-0" />
            {!collapsed && "Megnyitás"}
          </a>
          <button
            type="button"
            onClick={handleLogout}
            title={collapsed ? (t("admin.nav.logout") as string) : undefined}
            className={`w-full flex items-center py-2 rounded-md text-sm font-medium text-brand-600 hover:bg-red-50 hover:text-red-700 ${
              collapsed ? "justify-center px-0" : "gap-2.5 px-3"
            }`}
          >
            <LogOut size={16} className="shrink-0" />
            {!collapsed && t("admin.nav.logout")}
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-auto">
        {fullBleed ? (
          children
        ) : (
          <div className="p-8 max-w-6xl">{children}</div>
        )}
      </main>
    </div>
  );
}
